import { useQuery } from '@tanstack/react-query';
import { ExternalLinkIcon } from 'lucide-react';
import { Avatar, EmptyState, LoadingState, Panel, Pill } from '@bcecloud/portal-ui';
import {
  lookupUsersByEmails,
  personName,
  readMyIdentity,
  type EventContact,
  type EventPerson,
} from '@/lib/events';
import { MYEVENTS_PORTAL_URL } from '@/lib/launch';

export interface ClientSectionProps {
  readonly eventId: string;
  /** Client-side portal users assigned to the event (role client/partner). */
  readonly people: readonly EventPerson[];
  /** The event's Salesforce contacts, for display names and roles. */
  readonly contacts: readonly EventContact[];
  /** The addresses that can see this event on My Events. */
  readonly contactEmails: readonly string[];
  readonly loading: boolean;
  /** The section's Add button; absent for read-only viewers. */
  readonly action?: React.ReactNode;
  /** Absent → read-only: no Remove control (contractors view, staff manage). */
  readonly onRemove?: (userId: string) => void;
}

/** One client-side person: a portal user, a Salesforce contact, or both. */
interface ClientRow {
  readonly key: string;
  readonly email?: string;
  readonly user?: EventPerson;
  readonly contact?: EventContact;
}

/** Deep link that opens My Events as this customer, on this event. */
function viewAsUrl(eventId: string, person: EventPerson): string {
  const params = new URLSearchParams({
    viewAs: person._id,
    viewAsName: personName(person),
    viewAsEmail: person.email ?? '',
  });
  return `${MYEVENTS_PORTAL_URL}/events/${eventId}?${params.toString()}`;
}

/**
 * Deep link that PREVIEWS My Events for an address with no account yet —
 * client visibility is purely email-based, so this shows exactly what the
 * contact will see the day they first sign in.
 */
function previewUrl(eventId: string, email: string, name: string): string {
  const params = new URLSearchParams({ viewAsEmail: email, viewAsName: name });
  return `${MYEVENTS_PORTAL_URL}/events/${eventId}?${params.toString()}`;
}

/** An external link styled like the roster's action text. */
function PortalLink({ href, label }: { readonly href: string; readonly label: string }): React.ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-portal-small font-bold text-brand hover:underline"
    >
      {label}
      <ExternalLinkIcon aria-hidden="true" className="h-3.5 w-3.5" />
    </a>
  );
}

/**
 * Everyone on the client side of an event, in one place.
 *
 * Merges the two ways a client person can be attached: portal users assigned
 * to the event, and the Salesforce contacts whose addresses grant My Events
 * access (accounts are created on first sign-in, so a contact without an
 * account is still "on" the event). Rows are keyed by email so a contact who
 * already signed in shows once, with both identities.
 *
 * Portal actions (view-as, preview, account lookup) are internal-only — the
 * API refuses them for contractors, who still see the roster read-only.
 *
 * @param props - The event's client-side users, contacts and addresses.
 * @returns The client section of the Team & attendees tab.
 */
export function ClientSection({
  eventId,
  people,
  contacts,
  contactEmails,
  loading,
  action,
  onRemove,
}: ClientSectionProps): React.ReactElement {
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');

  const rows = new Map<string, ClientRow>();
  for (const user of people) {
    const email = user.email?.trim().toLowerCase();
    rows.set(email || user._id, { key: email || user._id, ...(email ? { email } : {}), user });
  }
  for (const raw of contactEmails) {
    const email = raw.trim().toLowerCase();
    if (!email) continue;
    const contact = contacts.find((candidate) => candidate.email?.trim().toLowerCase() === email);
    const existing = rows.get(email);
    rows.set(email, { key: email, email, ...existing, ...(contact ? { contact } : {}) });
  }

  // Contacts without an event user may still hold an account (My Events access
  // is email-based, not roster-based) — look those up to offer view-as.
  const lookupEmails = [...rows.values()]
    .filter((row) => row.user === undefined)
    .map((row) => row.email)
    .filter((email): email is string => email !== undefined)
    .sort();
  const accounts = useQuery({
    queryKey: ['portal-accounts', eventId, lookupEmails.join(',')],
    queryFn: ({ signal }) => lookupUsersByEmails(lookupEmails, signal),
    enabled: isInternal && lookupEmails.length > 0,
  });

  /** The right-hand portal action for a row — internal viewers only. */
  function portalAction(row: ClientRow): React.ReactElement | null {
    const account = row.user ?? (row.email ? accounts.data?.get(row.email) : undefined);
    if (account !== undefined) {
      return account.role === 'client' ? (
        <PortalLink href={viewAsUrl(eventId, account)} label="View their portal" />
      ) : (
        <span className="text-portal-micro text-portal-muted">
          {account.role} account — no customer portal
        </span>
      );
    }
    if (row.email === undefined) return null;
    if (accounts.isPending) {
      return <span className="text-portal-micro text-portal-muted">Checking…</span>;
    }
    return (
      <>
        <PortalLink
          href={previewUrl(eventId, row.email, row.contact?.name || row.email)}
          label="Preview their portal"
        />
        <span className="block text-portal-micro text-portal-muted">
          No account yet — access starts at first sign-in
        </span>
      </>
    );
  }

  return (
    <Panel>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-portal-line px-4 py-3">
        <div>
          <h2 className="text-portal-card-title font-bold">Client</h2>
          <p className="text-portal-micro text-portal-muted">
            Who this event belongs to on My Events — anyone listed gets access the first time
            they sign in with their address, no invitation needed.
          </p>
        </div>
        {action}
      </div>
      {loading ? (
        <LoadingState label="Loading client people…" />
      ) : rows.size === 0 ? (
        <EmptyState message="No client contacts yet — contacts come from the Salesforce opportunity." />
      ) : (
        <ul>
          {[...rows.values()].map((row) => {
            const user = row.user;
            const name = user ? personName(user) : row.contact?.name || row.email || '';
            return (
              <li
                key={row.key}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-portal-line px-4 py-2.5 last:border-b-0"
              >
                <Avatar name={name} size={32} />
                <span className="min-w-0 flex-1">
                  <b className="block truncate text-portal-small">{name}</b>
                  <span className="block truncate text-portal-micro text-portal-muted">
                    {[row.email ?? user?.email, row.contact?.role].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {user?.role ? (
                  <Pill tone={user.role === 'client' ? 'brand' : 'neutral'}>{user.role}</Pill>
                ) : null}
                {isInternal ? <span className="text-right">{portalAction(row)}</span> : null}
                {user && onRemove ? (
                  <button
                    type="button"
                    onClick={() => onRemove(user._id)}
                    className="text-portal-micro text-portal-muted hover:text-rose-700"
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
